import { Chip } from '@ketch-com/deck'
import { Typography, Box, styled } from '@mui/material'
import { useIdentitySpaces } from 'api/identitySpaces/queries/useIdentitySpaces'
import { ExperiencePreviewVariant } from 'pages/consentAndRights/experiences-v2/view/components/experiencePreview/constants'
import { ExperiencePreview } from 'pages/consentAndRights/experiences-v2/view/components/experiencePreview/ExperiencePreview'
import { formatDateTimeFromUnix } from 'utils/helpers/dateTime'
import { useQueryParams } from 'utils/hooks'
import { withExperiencePreviewContext } from 'pages/consentAndRights/experiences-v2/view/components/experiencePreview/context/ExperiencePreviewContext'
import { useAuditLogHistoricalConfig } from 'pages/insights/auditLog/hooks/useAuditLogHistoricalConfig'
import { Drawer } from 'components/ui-kit/drawer/Drawer'
import { useAuditLogContext } from 'pages/insights/auditLog/context/AuditLogContext'
import { GetConsentStatusResponseBodyPurposesItemDTO, PermitHistoryDTO } from '@ketch-com/figurehead'
import { PreviewMessageType } from '@ketch-sdk/ketch-types'
import { useMemo } from 'react'

export enum AuditLogExperienceVariant {
  Latest = 'latest',
}

const DrawerContentBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
})

/**
 * Component for the consent context drawer content.
 * @returns Consent context drawer content component
 */
const AuditLogExperienceDrawerContent: React.FC = () => {
  const {
    showViewExperienceModal,
    setShowViewExperienceModal,
    auditLogExperiencePreviewPermit: permit,
  } = useAuditLogContext()

  const isConsentTab = window.location.pathname.includes('/consent')
  const configId =
    (permit as GetConsentStatusResponseBodyPurposesItemDTO)?.context?.configurationId ||
    (permit as PermitHistoryDTO)?.configID ||
    ''

  const { config } = useAuditLogHistoricalConfig({
    configId,
  })

  const language = config?.language
  const jurisdiction = (permit as GetConsentStatusResponseBodyPurposesItemDTO)?.jurisdictionName

  const consentTime = formatDateTimeFromUnix(permit?.collectedAt)

  const { queries } = useQueryParams<{
    identifierSearch?: string
    identitySpaceCode?: string
    permitDateTime?: string
  }>()
  const { data: identitySpaces } = useIdentitySpaces()

  const identitySpaceName = identitySpaces?.find(space => space.code === queries.identitySpaceCode)?.name

  const experienceType = useMemo(() => {
    const source =
      (permit as GetConsentStatusResponseBodyPurposesItemDTO)?.context?.source || (permit as PermitHistoryDTO)?.source

    if (source?.includes('modal')) {
      return PreviewMessageType.ShowModal
    }

    if (source?.includes('preference')) {
      return PreviewMessageType.ShowPreference
    }

    return PreviewMessageType.ShowBanner
  }, [permit])

  return (
    <Drawer
      open={showViewExperienceModal}
      onClose={() => setShowViewExperienceModal(false)}
      sx={{ zIndex: 1400 }}
      PaperProps={{
        sx: {
          width: '100%',
          height: '90%',
          maxWidth: '1300px',
          margin: 'auto', // Centered
          padding: '32px',
          borderRadius: '37px 37px 0px 0px',
          overflowY: 'scroll',
        },
      }}
    >
      <DrawerContentBox>
        <Box display="flex" flexDirection="row" gap={1}>
          <Typography variant="h2" mb={1}>
            {isConsentTab ? 'Consent' : 'Subscription'} Experience Preview
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: '8px', mb: '12px' }}>
          <Chip
            label={
              <>
                <Typography variant="label">{identitySpaceName}: </Typography>
                <Typography variant="body">{queries.identifierSearch}</Typography>
              </>
            }
            sx={{ width: 'fit-content' }}
          />
          <Chip
            label={
              consentTime === 'latest' ? (
                <Typography variant="label">Latest</Typography>
              ) : (
                <>
                  <Typography variant="label">Date: </Typography>
                  <Typography variant="body">
                    {formatDateTimeFromUnix(new Date(consentTime || '').getTime() / 1000, 'MMM DD yyyy, h:mm A')}
                  </Typography>
                </>
              )
            }
            sx={{ width: 'fit-content' }}
          />
        </Box>
        <Typography mb={2}>Below are the configurations as of the time of this consent being generated.</Typography>
        <ExperiencePreview
          variant={ExperiencePreviewVariant.DirectConfig}
          previewConfig={config || undefined}
          headerData={{ language: language || 'None', jurisdiction: jurisdiction, experienceType }}
          experienceType={experienceType}
        />
      </DrawerContentBox>
    </Drawer>
  )
}

export const AuditLogExperienceDrawer = withExperiencePreviewContext(AuditLogExperienceDrawerContent)
